import React, { useEffect, useRef } from 'react';
import SectionHeading from './SectionHeading';
import { stats, brand } from '../mock/mock';
import { Sparkles } from 'lucide-react';

const HANUMAN_IMG =
  'https://images.unsplash.com/photo-1686582557983-0df22fd25187?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxIYW51bWFufGVufDB8fHx8MTc4ODAxOTM1M3ww&ixlib=rb-4.1.0&q=85';

export default function About() {
  const sectionRef = useRef(null);
  const hanumanRef = useRef(null);

  // Hanuman ji drifts with scroll while the About section is in view.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const sec = sectionRef.current;
          const han = hanumanRef.current;
          if (sec && han) {
            const rect = sec.getBoundingClientRect();
            const progress = 1 - (rect.top + rect.height / 2) / window.innerHeight;
            const sway = Math.sin(progress * Math.PI * 2) * 70;
            const rise = progress * 60;
            han.style.transform = `translate3d(${sway}px, ${-rise}px, 0)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 px-5 md:px-8 overflow-hidden"
    >
      {/* Gold & white heaven background */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(circle at 50% 20%, #ffffff 0%, #fff8e6 40%, #f9ecc9 75%, #f4e6c8 100%)',
        }}
      />
      {/* Soft heavenly clouds */}
      <div
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          backgroundImage:
            'radial-gradient(ellipse 40% 22% at 18% 18%, rgba(255,255,255,0.9), transparent 70%), radial-gradient(ellipse 45% 26% at 82% 32%, rgba(255,255,255,0.85), transparent 72%), radial-gradient(ellipse 55% 30% at 50% 88%, rgba(255,248,225,0.9), transparent 75%)',
        }}
      />
      {/* Hanuman ji moving with scroll */}
      <div
        ref={hanumanRef}
        className="absolute right-[4%] top-1/2 -translate-y-1/2 w-[460px] max-w-[55vw] -z-10 pointer-events-none will-change-transform"
        style={{ transition: 'transform 0.15s linear' }}
      >
        <img
          src={HANUMAN_IMG}
          alt="Lord Hanuman"
          className="w-full h-auto select-none"
          style={{
            opacity: 0.16,
            mixBlendMode: 'multiply',
            maskImage:
              'radial-gradient(ellipse 58% 66% at 50% 45%, #000 38%, transparent 78%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 58% 66% at 50% 45%, #000 38%, transparent 78%)',
          }}
          draggable={false}
        />
      </div>
      {/* faint mandala watermark */}
      <div
        className="absolute -left-32 bottom-10 w-[420px] h-[420px] rounded-full opacity-[0.05] animate-spin-slow -z-10"
        style={{
          background:
            'repeating-conic-gradient(#7c130f 0deg 6deg, transparent 6deg 12deg)',
        }}
      />
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="The Guiding Light"
          title="Vivechan Gautam"
          subtitle="A life devoted to decoding the divine language of numbers."
        />

        <div className="grid lg:grid-cols-2 gap-12 items-center mt-16">
          {/* Portrait card */}
          <div className="reveal reveal-left flex justify-center">
            <div
              className="relative rounded-3xl p-3 max-w-sm w-full"
              style={{
                background: 'linear-gradient(160deg, #fffaf0, #f0dcae)',
                border: '1.5px solid #d9a63b',
                boxShadow: '0 20px 50px rgba(124,19,15,0.15)',
              }}
            >
              <div
                className="rounded-2xl aspect-[4/5] flex flex-col items-center justify-center text-center p-8"
                style={{
                  background:
                    'radial-gradient(circle at 50% 30%, #a71c14 0%, #7c130f 55%, #4a0a07 100%)',
                }}
              >
                <Sparkles className="text-[#f2d68a] mb-4 animate-glow" size={54} strokeWidth={1.4} />
                <p className="font-display text-[#f2d68a] text-3xl font-bold">15+ Years</p>
                <p className="font-heading tracking-[0.25em] text-[#e2b84f] text-sm mt-2 uppercase">
                  Of Vedic Practice
                </p>
                <div className="ornament-line w-24 my-5" />
                <p className="font-body italic text-[#f4e6c8] text-lg leading-snug">
                  “Numbers never lie. They whisper the map of your destiny — we simply
                  help you read it.”
                </p>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="reveal reveal-right">
            <p className="font-body text-[#4a2f16] text-xl leading-relaxed">
              For nearly two decades, <strong className="text-[#7c130f]">Vivechan Gautam</strong> has
              guided seekers across India through the ancient sciences of Numerology,
              Astrology, Vastu and Vedic rituals. His approach blends timeless scriptural
              wisdom with practical, modern remedies — no fear, no superstition.
            </p>
            <p className="font-body text-[#6b4826] text-lg leading-relaxed mt-4">
              Every consultation is personal. From a newborn’s name to a business launch,
              from marriage compatibility to career direction, his readings have helped
              thousands realign with prosperity, peace and purpose.
            </p>

            <div className="grid grid-cols-2 gap-5 mt-9">
              {stats.map((st, i) => (
                <div
                  key={st.label}
                  className="reveal rounded-xl py-5 px-4 text-center"
                  data-delay={i * 100}
                  style={{
                    background: 'linear-gradient(180deg, #fffaf0, #f6e9cd)',
                    border: '1px solid #e2c887',
                  }}
                >
                  <p className="font-display font-bold text-3xl text-gold-gradient">{st.value}</p>
                  <p className="font-heading tracking-widest uppercase text-xs text-[#7c4a20] mt-1">
                    {st.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
